import { useState, useEffect, useCallback } from 'react'
import { appsyncQuery } from '../../api/appsync-client.js'
import { useSubscription } from '../../api/useSubscription.js'
import { REFLECTION_FIELDS, parseReflection, stringifyReflectionInput } from './useReflections.js'

const GET_REFLECTION = `
  query GetReflection($reflectionId: ID!, $ownerUserId: ID) {
    getReflection(reflectionId: $reflectionId, ownerUserId: $ownerUserId) {
      ${REFLECTION_FIELDS}
    }
  }
`

const UPDATE_REFLECTION = `
  mutation UpdateReflection($reflectionId: ID!, $ownerUserId: ID, $input: UpdateReflectionInput!) {
    updateReflection(reflectionId: $reflectionId, ownerUserId: $ownerUserId, input: $input) {
      ${REFLECTION_FIELDS}
    }
  }
`

const MARK_READY = `
  mutation MarkReflectionReady($reflectionId: ID!, $ownerUserId: ID) {
    markReflectionReady(reflectionId: $reflectionId, ownerUserId: $ownerUserId) {
      ${REFLECTION_FIELDS}
    }
  }
`

const SHARE_REFLECTION = `
  mutation ShareReflection($reflectionId: ID!, $ownerUserId: ID) {
    shareReflection(reflectionId: $reflectionId, ownerUserId: $ownerUserId) {
      ${REFLECTION_FIELDS}
    }
  }
`

const CLOSE_REFLECTION = `
  mutation CloseReflection($reflectionId: ID!, $ownerUserId: ID) {
    closeReflection(reflectionId: $reflectionId, ownerUserId: $ownerUserId) {
      ${REFLECTION_FIELDS}
    }
  }
`

const REOPEN_REFLECTION = `
  mutation ReopenReflection($reflectionId: ID!, $ownerUserId: ID) {
    reopenReflection(reflectionId: $reflectionId, ownerUserId: $ownerUserId) {
      ${REFLECTION_FIELDS}
    }
  }
`

const PROMOTE_COMMITMENT = `
  mutation PromoteCommitmentToTask($reflectionId: ID!, $commitmentId: ID!, $ownerUserId: ID, $input: CreateTaskInput!) {
    promoteCommitmentToTask(reflectionId: $reflectionId, commitmentId: $commitmentId, ownerUserId: $ownerUserId, input: $input) {
      ${REFLECTION_FIELDS}
    }
  }
`

const ON_REFLECTION_CHANGED = `
  subscription OnReflectionChanged($ownerUserId: ID!) {
    onReflectionChanged(ownerUserId: $ownerUserId) {
      ${REFLECTION_FIELDS}
    }
  }
`

export function useReflection({ reflectionId, ownerUserId = null, enabled = true } = {}) {
  const [reflection, setReflection] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!enabled || !reflectionId) return
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(GET_REFLECTION, { reflectionId, ownerUserId })
      setReflection(parseReflection(data.getReflection))
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [reflectionId, ownerUserId, enabled])

  useEffect(() => { load() }, [load])

  useSubscription(
    ON_REFLECTION_CHANGED,
    { ownerUserId },
    (data) => {
      const updated = parseReflection(data.onReflectionChanged)
      if (updated && updated.reflectionId === reflectionId) {
        setReflection(updated)
      }
    },
    { enabled: enabled && !!ownerUserId && !!reflectionId },
  )

  const updateReflection = useCallback(async (input) => {
    const data = await appsyncQuery(UPDATE_REFLECTION, {
      reflectionId,
      ownerUserId,
      input: stringifyReflectionInput(input),
    })
    const reflection = parseReflection(data.updateReflection)
    setReflection(reflection)
    return reflection
  }, [reflectionId, ownerUserId])

  const markReady = useCallback(async () => {
    const data = await appsyncQuery(MARK_READY, { reflectionId, ownerUserId })
    const reflection = parseReflection(data.markReflectionReady)
    setReflection(reflection)
    return reflection
  }, [reflectionId, ownerUserId])

  const shareReflection = useCallback(async () => {
    const data = await appsyncQuery(SHARE_REFLECTION, { reflectionId, ownerUserId })
    const reflection = parseReflection(data.shareReflection)
    setReflection(reflection)
    return reflection
  }, [reflectionId, ownerUserId])

  const closeReflection = useCallback(async () => {
    const data = await appsyncQuery(CLOSE_REFLECTION, { reflectionId, ownerUserId })
    const reflection = parseReflection(data.closeReflection)
    setReflection(reflection)
    return reflection
  }, [reflectionId, ownerUserId])

  const reopenReflection = useCallback(async () => {
    const data = await appsyncQuery(REOPEN_REFLECTION, { reflectionId, ownerUserId })
    const reflection = parseReflection(data.reopenReflection)
    setReflection(reflection)
    return reflection
  }, [reflectionId, ownerUserId])

  const promoteCommitment = useCallback(async (commitmentId, taskInput) => {
    const data = await appsyncQuery(PROMOTE_COMMITMENT, { reflectionId, commitmentId, ownerUserId, input: taskInput })
    const reflection = parseReflection(data.promoteCommitmentToTask)
    setReflection(reflection)
    return reflection
  }, [reflectionId, ownerUserId])

  return {
    reflection,
    loading,
    error,
    reload: load,
    updateReflection,
    markReady,
    shareReflection,
    closeReflection,
    reopenReflection,
    promoteCommitment,
  }
}
